import type { KeyboardEvent } from 'react';
import {
  addDays,
  addMonths,
  addYears,
  endOfMonth,
  endOfWeek,
  endOfYear,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subYears,
} from 'date-fns';

type Args = {
  e: KeyboardEvent<HTMLInputElement>;
  onDateChange: (date: Date) => void;
  baseDate: Date;
};
export const applyKeyboardShortcuts = (args: Args) => {
  // Year - 1
  if (args.e.ctrlKey && args.e.key === 'ArrowUp' && args.e.shiftKey) {
    args.e.preventDefault();
    args.onDateChange(subYears(args.baseDate, 1));
    return;
  }
  // Year + 1
  else if (args.e.ctrlKey && args.e.key === 'ArrowDown' && args.e.shiftKey) {
    args.e.preventDefault();
    args.onDateChange(addYears(args.baseDate, 1));
    return;
  }
  // Month - 1
  else if (args.e.altKey && args.e.key === 'ArrowUp' && args.e.shiftKey) {
    args.e.preventDefault();
    args.onDateChange(subMonths(args.baseDate, 1));
    return;
  }
  // Month + 1
  else if (args.e.altKey && args.e.key === 'ArrowDown' && args.e.shiftKey) {
    args.e.preventDefault();
    args.onDateChange(addMonths(args.baseDate, 1));
    return;
  }
  // Day + 1
  else if (args.e.altKey && args.e.key === 'ArrowRight') {
    args.e.preventDefault();
    args.onDateChange(addDays(args.baseDate, 1));
    return;
  }
  // Day - 1
  else if (args.e.altKey && args.e.key === 'ArrowLeft') {
    args.e.preventDefault();
    args.onDateChange(subDays(args.baseDate, 1));
    return;
  }
  // Day - 7
  else if (args.e.altKey && args.e.key === 'ArrowUp') {
    args.e.preventDefault();
    args.onDateChange(subDays(args.baseDate, 7));
    return;
  }
  // Day + 7
  else if (args.e.altKey && args.e.key === 'ArrowDown') {
    args.e.preventDefault();
    args.onDateChange(addDays(args.baseDate, 7));
    return;
  }

  /**
   * Make sure multi-key shorcuts are consumed above, after this only single
   *  key shorcuts are allowed.
   *
   * This is because we don't want to override default browser multi-key shortcuts.
   * For example we handle the "r" shortcut but `cmd + r` is a browser shortcut for
   *  reloading the tab. This allows us to keep both shortcuts alive.
   */
  if (args.e.ctrlKey || args.e.altKey || args.e.metaKey || args.e.shiftKey) {
    return;
  }

  switch (args.e.key) {
    case 't': {
      args.e.preventDefault();
      args.onDateChange(new Date());
      break;
    }
    case 'w':
    case 'W': {
      args.e.preventDefault();
      args.onDateChange(startOfWeek(args.baseDate));
      break;
    }
    case 'k':
    case 'K': {
      args.e.preventDefault();
      args.onDateChange(endOfWeek(args.baseDate));
      break;
    }
    case 'm':
    case 'M': {
      args.e.preventDefault();
      args.onDateChange(startOfMonth(args.baseDate));
      break;
    }
    case 'h':
    case 'H': {
      args.e.preventDefault();
      args.onDateChange(endOfMonth(args.baseDate));
      break;
    }
    case 'y':
    case 'Y': {
      args.e.preventDefault();
      args.onDateChange(startOfYear(args.baseDate));
      break;
    }
    case 'r':
    case 'R': {
      args.e.preventDefault();
      args.onDateChange(endOfYear(args.baseDate));
      break;
    }
  }
};
